import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdDeveloperBoard, MdMemory } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

export default function AddPlcModal({ companyId, onClose, onSuccess }) {
    const { t } = useLanguage();
    const [systems, setSystems] = useState([]);
    const [loadingSystems, setLoadingSystems] = useState(true);
    const [selectedSystemId, setSelectedSystemId] = useState('');
    const [plcName, setPlcName] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoadingSystems(true);
                // Fetch systems and existing PLCs in parallel
                const [sysRes, plcRes] = await Promise.all([
                    api.get(`/systems/company/${companyId}`),
                    api.get(`/plc/company/${companyId}`)
                ]);

                const allSystems = sysRes.data.data || [];
                const existingPlcs = plcRes.data.data || [];
                
                // Filter out systems that already have a PLC card
                const availableSystems = allSystems.filter(sys => 
                    !existingPlcs.some(plc => {
                        const plcSysId = plc.systemId?._id || plc.systemId;
                        return plcSysId && plcSysId.toString() === sys._id.toString();
                    })
                );

                setSystems(availableSystems);
                if (availableSystems.length > 0) {
                    setSelectedSystemId(availableSystems[0]._id);
                }
            } catch (err) {
                toast.error(t('addSensorModal.systemsLoadFailed'));
            } finally {
                setLoadingSystems(false);
            }
        };
        fetchData();
    }, [companyId, t]);

    const validate = () => {
        const errs = {};
        if (!selectedSystemId) errs.system = t('plc.systemRequired');
        if (!plcName.trim()) errs.plcName = t('plc.plcNameRequired');
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }

        try {
            setLoading(true);
            await api.post('/plc', {
                companyId,
                systemId: selectedSystemId,
                plcName: plcName.trim()
            });
            toast.success(t('plc.plcCreated'));
            onSuccess();
        } catch (err) {
            toast.error(err.response?.data?.message || t('addSensorModal.sensorAddFailed'));
        } finally {
            setLoading(false);
        }
    };

    const clearError = (field) => setErrors(prev => ({ ...prev, [field]: '' }));

    return (
        <AnimatePresence>
            <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={(e) => e.target === e.currentTarget && onClose()}>
                <motion.div className="modal" style={{ maxWidth: 440 }} initial={{ opacity: 0, scale: 0.9, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.25 }}>

                    <div className="modal-header">
                        <div className="modal-title">
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                                <MdMemory size={20} />
                            </div>
                            {t('plc.addPlcCard')}
                        </div>
                        <button className="modal-close" onClick={onClose} id="close-plc-modal"><MdClose /></button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            
                            {/* ── Sistem Seçimi ── */}
                            <div className="form-group">
                                <label className="form-label">
                                    <MdDeveloperBoard style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                    {t('addSensorModal.systemLabel')}
                                </label>
                                {loadingSystems ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 13, padding: '10px 0' }}>
                                        <span className="spinner spinner-sm" /> {t('common.loading')}
                                    </div>
                                ) : (
                                    systems.length > 0 ? (
                                        <select
                                            className="form-select"
                                            value={selectedSystemId}
                                            onChange={(e) => { setSelectedSystemId(e.target.value); clearError('system'); }}
                                        >
                                            {systems.map(s => (
                                                <option key={s._id} value={s._id}>{s.systemName}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div style={{ padding: '10px 12px', background: 'rgba(239,68,68,0.08)', color: 'var(--danger)', borderRadius: 8, fontSize: 13 }}>
                                            ⚠️ {t('plc.noSystems') || 'Tüm sistemlerde PLC kartı zaten tanımlı veya sistem yok.'}
                                        </div>
                                    )
                                )}
                                {errors.system && <div className="form-error">{errors.system}</div>}
                            </div>

                            {/* ── PLC Kart Adı ── */}
                            <div className="form-group">
                                <label className="form-label" htmlFor="plc-name">{t('plc.plcCardName')}</label>
                                <input
                                    id="plc-name"
                                    className="form-input"
                                    placeholder={t('plc.plcNamePlaceholder')}
                                    value={plcName}
                                    onChange={(e) => { setPlcName(e.target.value); clearError('plcName'); }}
                                    disabled={loading || systems.length === 0}
                                />
                                {errors.plcName && <div className="form-error">{errors.plcName}</div>}
                            </div>

                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>{t('common.cancel')}</button>
                            <button type="submit" className="btn btn-primary" disabled={loading || systems.length === 0} id="submit-plc-btn">
                                {loading ? <><span className="spinner spinner-sm" /> {t('common.adding')}</> : <><MdMemory style={{ marginRight: 6 }} />{t('common.add')}</>}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
